import { useId, useState } from "react";
import { ApiError } from "../api/client";
import {
  useBrandKits,
  useCreateBrandKit,
  useDeleteBrandKit,
  useRemoveDefaultBrandKit,
  useSetDefaultBrandKit,
  useUpdateBrandKit,
} from "../api/brandKits";
import { type MediaItem, useUploads } from "../api/media";
import {
  type BrandColour,
  type BrandKit,
  type BrandKitBody,
  MAX_VOICE_CHARS,
} from "../api/types";
import { messageFor } from "../copy/errors";
import { UploadButton } from "../library/UploadButton";
import {
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Pill,
  Select,
  Skeleton,
} from "../ui";

const HEX = /^#[0-9a-fA-F]{6}$/;
const CONFIRM_DELETE =
  "Delete this brand kit? Jobs made with it keep their briefs; new ones will not see it.";

/** A `validation_error` names its field as `detail.field`, e.g.
 *  `palette[0].hex`; the form shows the message on that group. */
export function fieldOf(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== "validation_error") {
    return null;
  }
  const detail = (error.detail ?? {}) as { field?: unknown };
  if (typeof detail.field !== "string") return null;
  return detail.field.replace(/\[.*$/, "").replace(/\..*$/, "");
}

export function emptyKit(): BrandKitBody {
  return {
    name: "",
    logo_asset_id: null,
    palette: [],
    fonts: [],
    voice: "",
    tagline: "",
    url: null,
  };
}

function bodyOf(kit: BrandKit): BrandKitBody {
  return {
    name: kit.name,
    logo_asset_id: kit.logo_asset_id,
    palette: kit.palette.map((c) => ({ ...c })),
    fonts: [...kit.fonts],
    voice: kit.voice,
    tagline: kit.tagline,
    url: kit.url,
  };
}

function Swatches({ palette }: { palette: BrandColour[] }) {
  if (palette.length === 0) return null;
  return (
    <ul className="m-0 flex list-none gap-1 p-0" aria-label="Palette">
      {palette.map((c, i) => (
        <li
          key={`${c.role}-${i}`}
          className="h-5 w-5 rounded-full border border-slate-200"
          style={{ backgroundColor: c.hex }}
          title={`${c.role} ${c.hex}`}
          aria-label={`${c.role} ${c.hex}`}
        />
      ))}
    </ul>
  );
}

function KitForm({
  initial,
  kitId,
  roles,
  fonts,
  maxColours,
  logos,
  first = false,
  onDone,
}: {
  initial: BrandKitBody;
  kitId: string | null;
  roles: string[];
  fonts: string[];
  maxColours: number;
  logos: MediaItem[];
  /** The org has no kit yet: the one made here becomes its default. */
  first?: boolean;
  onDone: () => void;
}) {
  const create = useCreateBrandKit();
  const update = useUpdateBrandKit();
  const [draft, setDraft] = useState<BrandKitBody>(initial);
  const logoId = useId();
  /** Logos uploaded from this form, shown as options until Media
   *  query catches up (and still listed if it answers slowly). */
  const [uploaded, setUploaded] = useState<{ id: string; name: string }[]>([]);
  const logoOptions = [
    ...logos.map((l) => ({ id: l.id, name: l.name })),
    ...uploaded.filter((u) => !logos.some((l) => l.id === u.id)),
  ];
  const pending = create.isPending || update.isPending;
  const error = kitId ? update.error : create.error;
  const errorField = fieldOf(error);
  const errorFor = (field: string) =>
    errorField === field && error instanceof ApiError ? error.message : null;

  const patch = (over: Partial<BrandKitBody>) =>
    setDraft((d) => ({ ...d, ...over }));
  const setColour = (i: number, over: Partial<BrandColour>) =>
    patch({
      palette: draft.palette.map((c, j) => (j === i ? { ...c, ...over } : c)),
    });

  const submit = () => {
    if (pending) return;
    const body: BrandKitBody = {
      ...draft,
      name: draft.name.trim(),
      url: draft.url?.trim() || null,
      voice: draft.voice.trim(),
      tagline: draft.tagline.trim(),
    };
    if (kitId) {
      update.mutate({ id: kitId, ...body }, { onSuccess: onDone });
    } else {
      create.mutate(body, { onSuccess: onDone });
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      aria-label={kitId ? "Edit brand kit" : "New brand kit"}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Field label="Name" error={errorFor("name")}>
        <Input
          value={draft.name}
          maxLength={100}
          required
          onChange={(e) => patch({ name: e.target.value })}
        />
      </Field>

      <div
        className={
          errorFor("palette") ? "tb-field tb-field-has-error" : "tb-field"
        }
      >
        <div className="tb-field-label">Palette</div>
        <ul
          className="m-0 flex list-none flex-col gap-2 p-0"
          aria-label="Colours"
        >
          {draft.palette.map((c, i) => (
            <li key={i} className="flex flex-wrap items-center gap-2">
              <Select
                value={c.role}
                aria-label={`Colour ${i + 1} role`}
                onChange={(e) => setColour(i, { role: e.target.value })}
              >
                {roles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
              <input
                type="color"
                aria-label={`Colour ${i + 1} picker`}
                value={HEX.test(c.hex) ? c.hex : "#000000"}
                className="h-9 w-12 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
                onChange={(e) => setColour(i, { hex: e.target.value })}
              />
              <Input
                value={c.hex}
                aria-label={`Colour ${i + 1} hex`}
                placeholder="#rrggbb"
                maxLength={7}
                className="w-28 font-mono"
                onChange={(e) => setColour(i, { hex: e.target.value })}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Remove colour ${i + 1}`}
                onClick={() =>
                  patch({ palette: draft.palette.filter((_, j) => j !== i) })
                }
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
        <div className="mt-2">
          <Button
            type="button"
            size="sm"
            disabled={draft.palette.length >= maxColours}
            title={
              draft.palette.length >= maxColours
                ? `Up to ${maxColours} colours`
                : undefined
            }
            onClick={() =>
              patch({
                palette: [
                  ...draft.palette,
                  {
                    role: roles[draft.palette.length] ?? "free",
                    hex: "#000000",
                  },
                ],
              })
            }
          >
            Add colour
          </Button>
        </div>
        {errorFor("palette") ? (
          <div className="tb-field-error" role="alert">
            {errorFor("palette")}
          </div>
        ) : (
          <div className="tb-field-helper">
            Up to {maxColours} colours as #rrggbb; the roles are the names the
            overlay uses.
          </div>
        )}
      </div>

      <div
        className={
          errorFor("fonts") ? "tb-field tb-field-has-error" : "tb-field"
        }
      >
        <div className="tb-field-label">Fonts</div>
        <div className="flex flex-wrap gap-3" role="group" aria-label="Fonts">
          {fonts.map((f) => (
            <label key={f} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.fonts.includes(f)}
                onChange={(e) =>
                  patch({
                    fonts: e.target.checked
                      ? [...draft.fonts, f]
                      : draft.fonts.filter((x) => x !== f),
                  })
                }
              />
              {f}
            </label>
          ))}
        </div>
        {errorFor("fonts") ? (
          <div className="tb-field-error" role="alert">
            {errorFor("fonts")}
          </div>
        ) : null}
      </div>

      <Field
        label="Voice"
        optional
        error={errorFor("voice")}
        helper={`One to three sentences, e.g. "calm, precise, no exclamation marks". ${draft.voice.length} / ${MAX_VOICE_CHARS}`}
      >
        <textarea
          className="tb-input min-h-[72px] resize-y"
          value={draft.voice}
          maxLength={MAX_VOICE_CHARS}
          onChange={(e) => patch({ voice: e.target.value })}
        />
      </Field>
      <Field label="Tagline" optional error={errorFor("tagline")}>
        <Input
          value={draft.tagline}
          maxLength={200}
          onChange={(e) => patch({ tagline: e.target.value })}
        />
      </Field>
      <Field label="Website" optional error={errorFor("url")}>
        <Input
          type="url"
          inputMode="url"
          placeholder="https://"
          value={draft.url ?? ""}
          onChange={(e) => patch({ url: e.target.value })}
        />
      </Field>
      <Field
        label="Logo"
        optional
        error={errorFor("logo_asset_id")}
        htmlFor={logoId}
        helper="An image from Media, or upload one now: PNG, JPEG or WebP."
      >
        {/* the row carries its own id so Field leaves the label pointing at the select */}
        <div id={`${logoId}-row`} className="flex flex-wrap items-center gap-2">
          <Select
            id={logoId}
            className="min-w-[12rem] flex-1"
            value={draft.logo_asset_id ?? ""}
            onChange={(e) => patch({ logo_asset_id: e.target.value || null })}
          >
            <option value="">None</option>
            {logoOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Select>
          <UploadButton
            label="Upload a logo"
            testId="logo-input"
            onUploaded={(assetId, file) => {
              setUploaded((list) => [
                ...list,
                { id: assetId, name: file.name },
              ]);
              patch({ logo_asset_id: assetId });
            }}
          />
        </div>
      </Field>

      {error && errorField === null ? (
        <p role="alert" className="m-0 text-sm text-slate-700">
          {error instanceof ApiError && error.code === "validation_error"
            ? error.message
            : messageFor(error instanceof ApiError ? error.code : undefined)}
        </p>
      ) : null}

      {first ? (
        <p className="m-0 text-sm text-slate-500" data-testid="first-kit-note">
          Your first kit becomes the default: it is applied to every new ad
          unless you pick another or None. You can remove the default later.
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          variant="primary"
          disabled={pending || !draft.name.trim()}
          aria-busy={pending}
        >
          {pending ? "Saving…" : kitId ? "Save changes" : "Create kit"}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** `/brand` (docs/15-product/brand-kits.md): the org's kits, one default,
 *  a form to create or edit, and a soft delete. */
export function BrandPage() {
  const kits = useBrandKits();
  const uploads = useUploads();
  const setDefault = useSetDefaultBrandKit();
  const removeDefault = useRemoveDefaultBrandKit();
  const remove = useDeleteBrandKit();
  const [editing, setEditing] = useState<"new" | string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const logos = uploads.data ?? [];
  const failure = setDefault.error ?? removeDefault.error ?? remove.error;

  const onDelete = (kit: BrandKit) => {
    if (busy) return;
    if (!window.confirm(CONFIRM_DELETE)) return;
    setBusy(kit.id);
    remove.mutate(kit.id, {
      onSettled: () => setBusy(null),
      onSuccess: () => {
        if (editing === kit.id) setEditing(null);
      },
    });
  };

  return (
    <>
      <PageHeader
        eyebrow="— Brand"
        title="Brand kits"
        right={
          kits.data && editing === null ? (
            <Button
              type="button"
              variant="primary"
              onClick={() => setEditing("new")}
            >
              New kit
            </Button>
          ) : null
        }
      />
      {kits.isPending ? (
        <Card aria-busy="true" className="flex flex-col gap-3 p-6">
          <Skeleton height={14} width="40%" />
          <Skeleton height={64} />
        </Card>
      ) : kits.isError ? (
        <Card role="alert" className="p-6 text-slate-700">
          {messageFor(
            kits.error instanceof ApiError ? kits.error.code : undefined,
          )}
        </Card>
      ) : (
        <div className="flex flex-col gap-6">
          {failure ? (
            <Card role="alert" className="p-4 text-slate-700">
              {messageFor(
                failure instanceof ApiError ? failure.code : undefined,
              )}
            </Card>
          ) : null}

          {editing === "new" ? (
            <Card className="p-6">
              <h2 className="tb-h-row m-0 mb-4">New brand kit</h2>
              <KitForm
                initial={emptyKit()}
                kitId={null}
                roles={kits.data.roles}
                fonts={kits.data.fonts}
                maxColours={kits.data.max_colours}
                logos={logos}
                first={kits.data.items.length === 0}
                onDone={() => setEditing(null)}
              />
            </Card>
          ) : null}

          {kits.data.items.length === 0 && editing !== "new" ? (
            <Card className="flex flex-col items-start gap-3 p-8">
              <p className="m-0 text-lg font-semibold text-slate-800">
                No brand kit yet.
              </p>
              <p className="m-0 text-slate-600">
                Keep your logo, palette, fonts and voice in one place and every
                brief starts from them. The default kit applies to every ad
                unless you pick another in the composer.
              </p>
              <Button
                type="button"
                variant="primary"
                onClick={() => setEditing("new")}
              >
                Create a brand kit
              </Button>
            </Card>
          ) : null}

          {kits.data.items.map((kit) => (
            <Card
              key={kit.id}
              className="flex flex-col gap-3 p-6"
              data-testid={`kit-${kit.id}`}
              aria-busy={busy === kit.id}
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-lg font-semibold text-slate-900">
                  {kit.name}
                </span>
                {kit.is_default ? (
                  <Pill size="sm" tone="purple">
                    default
                  </Pill>
                ) : null}
                <Swatches palette={kit.palette} />
                <span className="text-xs text-slate-500">
                  {kit.fonts.length > 0 ? kit.fonts.join(", ") : "no fonts"}
                  {kit.tagline ? ` · ${kit.tagline}` : ""}
                </span>
                <span className="ml-auto flex flex-wrap gap-2">
                  {kit.is_default ? (
                    <Button
                      type="button"
                      size="sm"
                      disabled={busy !== null}
                      aria-label={`Remove the default from ${kit.name}`}
                      onClick={() => {
                        setBusy(kit.id);
                        removeDefault.mutate(kit.id, {
                          onSettled: () => setBusy(null),
                        });
                      }}
                    >
                      Remove default
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      disabled={busy !== null}
                      aria-label={`Make ${kit.name} the default`}
                      onClick={() => {
                        setBusy(kit.id);
                        setDefault.mutate(kit.id, {
                          onSettled: () => setBusy(null),
                        });
                      }}
                    >
                      Set default
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={`Edit ${kit.name}`}
                    onClick={() =>
                      setEditing(editing === kit.id ? null : kit.id)
                    }
                  >
                    {editing === kit.id ? "Close" : "Edit"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={busy !== null}
                    aria-label={`Delete ${kit.name}`}
                    onClick={() => onDelete(kit)}
                  >
                    {busy === kit.id && remove.isPending
                      ? "Deleting…"
                      : "Delete"}
                  </Button>
                </span>
              </div>
              {kit.is_default ? (
                <p
                  className="m-0 text-sm text-slate-500"
                  data-testid="default-note"
                >
                  The default kit is applied to every new ad unless you pick
                  another or None in the composer. Remove the default and a new
                  ad has no brand kit until you pick one.
                </p>
              ) : null}
              {kit.voice ? (
                <p className="m-0 text-sm text-slate-600">{kit.voice}</p>
              ) : null}
              {editing === kit.id ? (
                <div className="border-t border-slate-200 pt-4">
                  <KitForm
                    key={kit.updated_at}
                    initial={bodyOf(kit)}
                    kitId={kit.id}
                    roles={kits.data.roles}
                    fonts={kits.data.fonts}
                    maxColours={kits.data.max_colours}
                    logos={logos}
                    onDone={() => setEditing(null)}
                  />
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
